import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import NoteCard from "@/components/app/NoteCard";
import OfferDialog from "@/components/app/OfferDialog";
import FeaturedRail from "@/components/app/FeaturedRail";
import { ArrowRight, Shield, TrendingUp, Layers } from "lucide-react";
import { useSEO, jsonLdFAQ } from "@/lib/seo";

const HERO_IMG = "https://images.pexels.com/photos/7985245/pexels-photo-7985245.jpeg";
const INVESTOR_IMG = "https://images.pexels.com/photos/5831265/pexels-photo-5831265.jpeg";

const HOME_FAQS = [
  {
    q: "What is a vehicle-backed note?",
    a: "A vehicle-backed note is a promissory note secured by a car, truck, or SUV title. When a borrower finances a vehicle, the resulting loan is packaged as a note that pays weekly or monthly cash flow to whoever holds it. Because the underlying vehicle title serves as collateral, notes are asset-backed, and holders can repossess and resell the vehicle if the borrower defaults."
  },
  {
    q: "What kind of yield do FinanceLocal notes produce?",
    a: "Yields on FinanceLocal typically range from 15% to 28% APR-equivalent, depending on the note's grade (A–D), category (performing, semi-performing, non-performing, repo-ready, or fresh origination), and how deep the buyer negotiates below the remaining principal balance."
  },
  {
    q: "How is FinanceLocal different from a typical note broker?",
    a: "FinanceLocal is a true peer-to-peer marketplace, not a broker. Sellers list notes directly, buyers submit offers, and the platform provides document management, Stripe escrow, and manual credit review. There are no hidden commissions on the note price itself — investors and dealers negotiate net of any platform fees."
  },
  {
    q: "Who can buy or sell notes on FinanceLocal?",
    a: "Any verified U.S. user account can buy and sell notes. Dealers get additional origination workflows to source vehicles for pre-approved borrowers. Note investing may involve accredited-investor considerations depending on how a listing is structured; investors self-certify per applicable federal and state rules."
  },
  {
    q: "How is the $2,500 escrow used?",
    a: "For pre-approval origination, the $2,500 Stripe escrow is a refundable commitment that establishes an investor's seriousness before a dealer match. If the funder misses the 2-business-day funding deadline after a vehicle is agreed on, the deposit is forfeited: 40% to the dealer, 40% to FinanceLocal, and 20% to the applicant."
  }
];

export default function Home() {
  const [stats, setStats] = useState({ total_notes: 0, outstanding_balance: 0, average_yield: 0, performing_notes: 0 });
  const [featured, setFeatured] = useState({ top_performing: [], top_yield: [], fresh_originations: [] });
  const [offerNote, setOfferNote] = useState(null);
  const navigate = useNavigate();

  useSEO({
    title: "FinanceLocal — Buy & Sell Cash-Flowing Vehicle Notes | Auto Note Marketplace",
    description: "FinanceLocal is the peer-to-peer marketplace for vehicle-backed auto notes. Browse performing car notes with weekly cash flow and yields up to 28%. Buy, sell, or fund new originations.",
    canonical: "https://myfinancelocal.com/",
    keywords: "vehicle-backed notes, car note investing, cash-flow notes, note marketplace, buy performing notes, passive income investing, seller financing notes, auto note buyers, dealer financing, note investors",
    jsonLd: jsonLdFAQ(HOME_FAQS),
  });

  useEffect(() => {
    api.get("/notes/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/notes/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-2xl text-white">
            <div className="font-overline text-accent">Vehicle-Backed Notes · Peer-to-Peer Marketplace</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95] tracking-tight">
              Buy & Sell Cash-Flowing Vehicle Notes
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl">
              The peer-to-peer marketplace for vehicle-backed notes with yields up to <span className="font-mono-data text-accent">28%</span>. List notes for sale, place offers, accept or counter — all without paperwork friction.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button data-testid="hero-view-notes" onClick={() => navigate("/notes")}
                className="rounded-none uppercase tracking-wider h-12 px-8 text-sm">
                Browse Available Notes <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button data-testid="hero-create-account" variant="outline"
                onClick={() => navigate("/login?signup=1")}
                className="rounded-none uppercase tracking-wider h-12 px-8 text-sm bg-transparent text-white border-white hover:bg-white hover:text-black">
                Create Free Account
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* STATS - bento grid */}
      <section className="border-y border-border bg-card">
        <div className="mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-4 grid-borders" data-testid="stats-grid">
          <StatTile label="Total Notes Available" value={stats.total_notes} testId="stat-total-notes" />
          <StatTile label="Outstanding Balance" value={fmtMoney(stats.outstanding_balance)} testId="stat-outstanding" />
          <StatTile label="Average Yield" value={fmtPct(stats.average_yield)} testId="stat-avg-yield" highlight />
          <StatTile label="Performing Notes" value={stats.performing_notes} testId="stat-performing" />
        </div>
      </section>

      {/* FEATURED RAIL — homepage only, up to 20 active listings */}
      <FeaturedRail onOffer={setOfferNote} />

      {/* FEATURED */}
      <FeaturedSection title="Top Performing" subtitle="Strong, consistent payment histories"
        notes={featured.top_performing} onOffer={setOfferNote} testId="featured-performing" />
      <FeaturedSection title="Highest Yield" subtitle="Outsized cash flow potential"
        notes={featured.top_yield} onOffer={setOfferNote} testId="featured-yield" />
      <FeaturedSection title="Fresh Originations" subtitle="Recently originated notes"
        notes={featured.fresh_originations} onOffer={setOfferNote} testId="featured-fresh" />

      {/* VALUE PROP */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="font-overline text-accent">Why FinanceLocal</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 tracking-tight">A new asset class, built for serious investors.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Diversify beyond equities and real estate. Vehicle notes deliver weekly cash flow, asset-backed security, and yields traditionally reserved for institutional capital.
            </p>
            <div className="mt-10 space-y-6">
              <Feature icon={Shield} title="Asset-Backed" body="Every note is collateralized by the underlying vehicle title." />
              <Feature icon={TrendingUp} title="Monthly Cash Flow" body="Receive payments throughout the life of the note." />
              <Feature icon={Layers} title="Tiered by Grade" body="A–D grading reflects rigorous risk evaluation." />
            </div>
          </div>
          <div className="border border-border">
            <img src={INVESTOR_IMG} alt="" className="w-full aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      <OfferDialog note={offerNote} open={!!offerNote} onOpenChange={(o) => !o && setOfferNote(null)} />
    </div>
  );
}

function StatTile({ label, value, testId, highlight }) {
  return (
    <div className="p-8 lg:p-10" data-testid={testId}>
      <div className="font-overline text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-4xl sm:text-5xl mt-3 font-light tracking-tighter ${highlight ? "text-primary" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function FeaturedSection({ title, subtitle, notes, onOffer, testId }) {
  if (!notes || notes.length === 0) return null;
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16" data-testid={testId}>
      <div className="flex items-end justify-between mb-8 border-b border-border pb-4">
        <div>
          <div className="font-overline text-accent">Featured</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2">{title}</h2>
          <p className="text-muted-foreground mt-1">{subtitle}</p>
        </div>
        <Link to="/notes" className="hidden sm:flex items-center gap-2 text-sm font-overline hover:text-primary">
          View All <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {notes.map((n) => <NoteCard key={n.note_id} note={n} onMakeOffer={onOffer} />)}
      </div>
    </section>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-4 border-l-2 border-primary pl-5">
      <div>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          <div className="font-display text-lg">{title}</div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">{body}</p>
      </div>
    </div>
  );
}
